'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { MessageCircle } from 'lucide-react';
import { siteConfig } from '@/lib/constants';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { AuthServiceError, isAuthenticated, requestOtp, verifyOtp } from '@/lib/auth/auth-service';
import {
  AUTH_STORAGE_KEYS,
  DEFAULT_OTP_RESEND_COOLDOWN_SECONDS,
  OTP_LENGTH,
} from '@/lib/auth/constants';
import { formatPhoneForCountryInput, isValidPhone, normalizePhone } from '@/lib/auth/auth-utils';

type LoginStep = 'phone' | 'otp';

const COUNTRY_OPTIONS = [{ code: 'CO', dialCode: '+57', flag: '🇨🇴', name: 'Colombia' }] as const;

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const nextParam = searchParams.get('next');
  const [step, setStep] = useState<LoginStep>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const breadcrumbItems = useMemo(
    () => [
      { name: 'Inicio', href: siteConfig.url },
      { name: 'Login', href: `${siteConfig.url}/login/` },
    ],
    []
  );

  useEffect(() => {
    if (isAuthenticated()) {
      router.replace(nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard');
      return;
    }

    const storedPhone = window.localStorage.getItem(AUTH_STORAGE_KEYS.loginPhone);
    const storedStep = window.localStorage.getItem(AUTH_STORAGE_KEYS.loginStep);
    const storedChallengeId = window.localStorage.getItem(AUTH_STORAGE_KEYS.loginChallengeId);
    const storedResendAt = window.localStorage.getItem(AUTH_STORAGE_KEYS.otpResendAt);

    if (storedPhone) {
      setPhone(formatPhoneForCountryInput(storedPhone).slice(0, 10));
    }

    if (storedStep === 'otp' && storedPhone) {
      setStep('otp');
    }

    if (storedChallengeId) {
      setChallengeId(storedChallengeId);
    }

    if (storedResendAt) {
      const resendAt = Number(storedResendAt);

      if (Number.isFinite(resendAt) && resendAt > Date.now()) {
        setResendAvailableAt(resendAt);
      } else {
        window.localStorage.removeItem(AUTH_STORAGE_KEYS.otpResendAt);
      }
    }
  }, [nextParam, router]);

  useEffect(() => {
    if (!resendAvailableAt) {
      setSecondsLeft(0);
      return;
    }

    const updateSeconds = () => {
      const remaining = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setSecondsLeft(remaining);

      if (remaining <= 0) {
        setResendAvailableAt(null);
        window.localStorage.removeItem(AUTH_STORAGE_KEYS.otpResendAt);
      }
    };

    updateSeconds();
    const intervalId = window.setInterval(updateSeconds, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendAvailableAt]);

  const startResendCountdown = (cooldownSeconds: number) => {
    const resendAt = Date.now() + cooldownSeconds * 1000;
    setResendAvailableAt(resendAt);
    window.localStorage.setItem(AUTH_STORAGE_KEYS.otpResendAt, String(resendAt));
  };

  const handleRequestOtp = async () => {
    const normalizedPhone = normalizePhone(phone);

    if (!isValidPhone(normalizedPhone)) {
      setErrorMessage('Ingresa un numero de WhatsApp colombiano valido, por ejemplo 3001234567.');
      return;
    }

    setErrorMessage(null);
    setInfoMessage(null);
    setIsSending(true);

    try {
      const response = await requestOtp(normalizedPhone);
      const cooldownSeconds = Number(process.env.NEXT_PUBLIC_OTP_RESEND_COOLDOWN_SECONDS);
      const effectiveCooldown = Number.isFinite(cooldownSeconds) && cooldownSeconds > 0
        ? cooldownSeconds
        : DEFAULT_OTP_RESEND_COOLDOWN_SECONDS;

      setPhone(formatPhoneForCountryInput(normalizedPhone).slice(0, 10));
      setChallengeId(response.challengeId ?? null);
      setStep('otp');
      setOtp('');
      setInfoMessage(response.message);
      window.localStorage.setItem(AUTH_STORAGE_KEYS.loginPhone, normalizedPhone);
      window.localStorage.setItem(AUTH_STORAGE_KEYS.loginStep, 'otp');

      if (response.challengeId) {
        window.localStorage.setItem(AUTH_STORAGE_KEYS.loginChallengeId, response.challengeId);
      } else {
        window.localStorage.removeItem(AUTH_STORAGE_KEYS.loginChallengeId);
      }

      startResendCountdown(effectiveCooldown);
    } catch (error) {
      if (error instanceof AuthServiceError) {
        setErrorMessage(error.message);
        return;
      }

      setErrorMessage('No se pudo enviar el código por WhatsApp.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendOtpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await handleRequestOtp();
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setInfoMessage(null);

    if (otp.length !== OTP_LENGTH) {
      setErrorMessage(`Ingresa un código OTP de ${OTP_LENGTH} dígitos.`);
      return;
    }

    setIsVerifying(true);
    try {
      await verifyOtp({
        challengeId: challengeId ?? undefined,
        phone: normalizePhone(phone),
        otp,
      });

      window.localStorage.removeItem(AUTH_STORAGE_KEYS.loginStep);
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.loginChallengeId);
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.otpResendAt);

      const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard';
      router.replace(safeNext);
    } catch (error) {
      if (error instanceof AuthServiceError) {
        setErrorMessage(error.message);
        return;
      }

      setErrorMessage('No se pudo validar el código OTP.');
    } finally {
      setIsVerifying(false);
    }
  };

  const resendDisabled = isSending || isVerifying || secondsLeft > 0;

  const handleBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setChallengeId(null);
    setErrorMessage(null);
    setInfoMessage(null);
    setResendAvailableAt(null);
    setSecondsLeft(0);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.loginStep);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.loginChallengeId);
    window.localStorage.removeItem(AUTH_STORAGE_KEYS.otpResendAt);
  };

  return (
    <main className="min-h-screen">
      <div className="container mx-auto px-6 pt-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <section className="py-12 md:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h1 className="text-section lg:text-5xl font-heading font-bold mb-4">
              Login
            </h1>
            <p className="text-body lg:text-lg text-text-secondary">
              Ingresa con tu número celular y un código será enviado a tu whatsapp (OTP)
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">
                  {step === 'otp' ? 'Verifica tu código' : 'Ingresa tu número'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {step === 'otp' ? (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div>
                      <label htmlFor="otp" className="block text-sm font-semibold text-text-primary mb-2">
                        Código OTP recibido por WhatsApp
                      </label>
                      <input
                        type="text"
                        id="otp"
                        name="otp"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                        required
                        className="w-full px-4 py-3 bg-bg-primary border border-border rounded-button text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="123456"
                      />
                      <p className="text-xs text-text-secondary mt-2">
                        Enviado a {COUNTRY_OPTIONS[0].dialCode} {formatPhoneForCountryInput(phone)}
                      </p>
                    </div>

                    <Button
                      size="lg"
                      className="w-full"
                      type="submit"
                      disabled={isVerifying || isSending || otp.length < OTP_LENGTH}
                    >
                      {isVerifying ? 'Validando...' : 'Verificar e ingresar'}
                    </Button>

                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      onClick={handleRequestOtp}
                      disabled={resendDisabled}
                    >
                      {secondsLeft > 0 ? `Reenviar en ${secondsLeft}s` : 'Reenviar código por WhatsApp'}
                    </Button>

                    <button
                      type="button"
                      onClick={handleBackToPhone}
                      className="text-sm text-text-secondary hover:text-accent transition-colors"
                    >
                      Cambiar número
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSendOtpSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-text-primary mb-2">
                        Número de WhatsApp
                      </label>
                      <div className="flex min-h-12 overflow-hidden rounded-button border border-border bg-bg-primary focus-within:ring-2 focus-within:ring-accent">
                        <select
                          aria-label="Pais"
                          value={COUNTRY_OPTIONS[0].code}
                          onChange={() => undefined}
                          className="w-28 shrink-0 border-r border-border bg-bg-secondary px-3 text-sm font-semibold text-text-primary outline-none"
                        >
                          {COUNTRY_OPTIONS.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.dialCode}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={phone}
                          onChange={(event) =>
                            setPhone(formatPhoneForCountryInput(event.target.value).slice(0, 10))
                          }
                          required
                          className="min-w-0 flex-1 bg-transparent px-4 py-3 text-text-primary placeholder:text-text-secondary/60 focus:outline-none"
                          placeholder="3001234567"
                        />
                      </div>
                    </div>

                    <Button size="lg" className="w-full" type="submit" disabled={isSending}>
                      {isSending ? 'Enviando código...' : 'Enviar OTP por WhatsApp'}
                    </Button>
                  </form>
                )}

                {errorMessage && <p className="text-sm text-error mt-4">{errorMessage}</p>}
                {infoMessage && !errorMessage && <p className="text-sm text-success mt-4">{infoMessage}</p>}
              </CardContent>
            </Card>

            <Card className="border-accent/50">
              <CardContent className="pt-6 space-y-5">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon icon={MessageCircle} size={24} className="text-accent" />
                </div>
                <h2 className="text-2xl font-heading font-semibold">Acceso rápido y seguro</h2>
                <ul className="space-y-3 text-text-secondary">
                  <li>Login solo con número celular y whatsapp.</li>
                  <li>Sin enlaces de registro.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
}
